import { buildIdpfUrl, type FromParam } from '@campaign/utils/buildIdpfUrl';

const PCC_IDPF_ENDPOINT = import.meta.env.PCC_IDPF_ENDPOINT;
const PCC_SIGNUP_URL = import.meta.env.PCC_SIGNUP_URL;
const PCC_FRONTEND_SYSTEM_ID = import.meta.env.PCC_FRONTEND_SYSTEM_ID;

const LOGIN_URL = `${PCC_IDPF_ENDPOINT}/login`;
const REGISTER_URL = PCC_SIGNUP_URL;
const MEMBER_MENU_URL = `${PCC_SIGNUP_URL}/edit`;

const CAMPAIGN_DETAIL_PATH_PREFIX = '/campaignDetail/';
const FROM_PARAM_PATTERN = /^[0-9a-zA-Z-]+$/;

/**
 * Minimal location shape used to build IDPF redirect URLs without depending on a router.
 */
export type LocationLike = {
  pathname: string;
  search: string;
};

const getBrowserLocation = (): LocationLike => ({
  pathname: window.location.pathname,
  search: window.location.search,
});

/**
 * Returns whether the given pathname is a campaign detail page.
 *
 * @param pathname - Current pathname (e.g. `/campaignDetail/organizationId/id`).
 * @returns `true` when the path includes `/campaignDetail/`.
 */
export function isCampaignDetailPath(pathname: string): boolean {
  return pathname.includes(CAMPAIGN_DETAIL_PATH_PREFIX);
}

/**
 * Extracts and validates the `from` query param from a search string.
 *
 * @remarks
 * Matches campaign-pf-web-app `hasValidFromParam`: only alphanumeric characters and hyphens are accepted.
 *
 * @param search - Query string, with or without a leading `?`.
 * @returns The validated `from` value, or `undefined` when missing or invalid.
 */
export function getValidFromParam(search: string): string | undefined {
  const from = new URLSearchParams(search).get('from') ?? undefined;
  if (!from || !FROM_PARAM_PATTERN.test(from)) {
    return undefined;
  }
  return from;
}

const redirectParams = () => ({
  frontsystemId: PCC_FRONTEND_SYSTEM_ID,
  redirectBaseUrl: location.origin,
});

/**
 * Builds the IDPF login URL for the campaign customer app.
 *
 * @remarks
 * On campaign detail pages, `currentPath` and a valid `from` param are included in `redirectUrl`
 * so the user returns to the same detail page after authentication. On other pages, `redirectUrl`
 * is the app origin only.
 *
 * @param fromParam - Optional explicit `from` value; when omitted on a detail page, read from `location.search`.
 * @param location - Location to derive path and query from; defaults to `window.location`.
 * @returns Full IDPF login URL with `frontsystemId`, `redirectUrl`, and optional `from`.
 */
export function buildLoginRedirectUrl(
  fromParam?: FromParam,
  location: LocationLike = getBrowserLocation()
): string {
  const isDetailPage = isCampaignDetailPath(location.pathname);
  const from =
    fromParam ??
    (isDetailPage ? getValidFromParam(location.search) : undefined);

  if (isDetailPage) {
    return buildIdpfUrl({
      idpfUrl: LOGIN_URL,
      ...redirectParams(),
      currentPath: location.pathname,
      fromParam: from,
    });
  }

  return buildIdpfUrl({
    idpfUrl: LOGIN_URL,
    ...redirectParams(),
    currentPath: '',
    fromParam: from,
  });
}

/**
 * Builds the IDPF member-registration URL for the campaign customer app.
 *
 * @remarks
 * Opens registration with a `redirectUrl` back to the app. On campaign detail pages,
 * the detail path and valid `from` param are preserved; otherwise `redirectUrl` is the app origin.
 *
 * @param location - Location to derive path and query from; defaults to `window.location`.
 * @returns Full IDPF register URL with `frontsystemId`, `redirectUrl`, and optional `from`.
 */
export function buildRegisterRedirectUrl(
  location: LocationLike = getBrowserLocation()
): string {
  const isDetailPage = isCampaignDetailPath(location.pathname);

  return buildIdpfUrl({
    idpfUrl: REGISTER_URL,
    ...redirectParams(),
    currentPath: isDetailPage ? location.pathname : '',
    fromParam: isDetailPage ? getValidFromParam(location.search) : undefined,
  });
}

/**
 * Builds the IDPF member-menu (profile edit) URL for the campaign customer app.
 *
 * @remarks
 * Navigates to the member menu with a `redirectUrl` back to the app. On campaign detail pages,
 * the detail path and valid `from` param are preserved; otherwise `redirectUrl` is the app origin.
 *
 * @param location - Location to derive path and query from; defaults to `window.location`.
 * @returns Full IDPF member-menu URL with `frontsystemId`, `redirectUrl`, and optional `from`.
 */
export function buildMemberMenuRedirectUrl(
  location: LocationLike = getBrowserLocation()
): string {
  const isDetailPage = isCampaignDetailPath(location.pathname);

  return buildIdpfUrl({
    idpfUrl: MEMBER_MENU_URL,
    ...redirectParams(),
    currentPath: isDetailPage ? location.pathname : '',
    fromParam: isDetailPage ? getValidFromParam(location.search) : undefined,
  });
}
